import React, { useState } from 'react';
import { DataGridPro, GridColDef, GridSortItem, GridSortModel } from '@mui/x-data-grid-pro';
import { Button, SelectChangeEvent, Tooltip } from '@mui/material';
import { getISOWeek } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faBan, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { FasitDataGridToolbar } from '../../../../components/data-grid/fasit-data-grid-toolbar';
import { attendanceAreaPathName, getLessonEditPath } from '../../attendance-area-pathnames';
import { ClassroomDto, EducatorDto, LessonDto, LessonStateDto } from '../../../../api-service/clients';
import { formatDate, toStartOfDay } from '../../../../utilities';
import { getLessonStateDisplayValue, isAttendanceSheetLocked, toSchultzDate } from '../../utils';
import { ConfirmDialog } from '../../../../components/confirm-dialog';
import { TextField } from '../../../../components/text-field';
import { DeleteLessonDialog } from '../../lesson/components/delete-lesson-dialog';

interface ILectureDataGridProps {
  lessons?: LessonDto[];
  onAddLesson: () => void;
  onRefreshLessons: () => void;
  classRooms: ClassroomDto[];
  educators: EducatorDto[];
  onPublishDraftLessonClicked: (lessonIds: string[]) => void;
  isSavingPublished: boolean;
  onCancelLessonsClicked: (lessonIds: string[], cancellationReason: string) => void;
  isSavingCancelled: boolean;
  onDeleteLessonsClicked: (lessonIds: string[]) => void;
  isDeletingLessons: boolean;
  isCourseCompleted: boolean;
}

const filterValues = [
  { text: 'Vis aktuelle og planlagte', value: 'open' },
  { text: 'Vis alle', value: 'all' },
];

export function LessonDataGrid(props: ILectureDataGridProps) {
  const {
    lessons,
    onAddLesson,
    onRefreshLessons,
    classRooms,
    educators,
    onPublishDraftLessonClicked,
    isSavingPublished,
    onCancelLessonsClicked,
    isSavingCancelled,
    onDeleteLessonsClicked,
    isDeletingLessons,
    isCourseCompleted,
  } = props;
  const [filterValue, setFilterValue] = useState('open');
  const [selectedLessons, setSelectedLessons] = useState<LessonDto[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationReasonError, setCancellationReasonError] = useState(false);
  const open = filterValue === 'open';

  const [sortModel, setSortModel] = useState<GridSortItem[]>([
    {
      field: 'beginAt',
      sort: 'asc',
    },
  ] as GridSortItem[]);

  function handleFilterChange(event: SelectChangeEvent): void {
    setFilterValue(event.target.value);
    setSortModel([{ field: 'beginAt', sort: 'asc' }]);
  }

  function handleEditLesson(lessonId: string) {
    window.open(`${attendanceAreaPathName}/${getLessonEditPath(lessonId)}`);
  }

  function handleConfirmClick() {
    const lessonIds = selectedLessons.filter(lesson => lesson.lessonState === LessonStateDto.Draft).map(lesson => lesson.lessonId);
    onPublishDraftLessonClicked(lessonIds);
    setShowConfirm(false);
  }
  function handleCancelClick() {
    setShowConfirm(false);
  }
  function handleCancelLessonsConfirmClick() {
    if (!cancellationReason.trim()) {
      setCancellationReasonError(true);
      return;
    }

    const lessonIds = selectedLessons
      .filter(lesson => lesson.lessonState === LessonStateDto.Published && !isAttendanceSheetLocked(lesson.attendanceSheetState))
      .map(lesson => lesson.lessonId);
    onCancelLessonsClicked(lessonIds, cancellationReason);
    setShowCancelConfirm(false);
    setCancellationReason('');
  }
  function handleCancelLessonsDismissClick() {
    setShowCancelConfirm(false);
    setCancellationReason('');
  }
  function handleDeleteLessonsConfirmClick() {
    const lessonIds = selectedLessons
      .filter(lesson => lesson.lessonState === LessonStateDto.Published && !isAttendanceSheetLocked(lesson.attendanceSheetState))
      .map(lesson => lesson.lessonId);
    onDeleteLessonsClicked(lessonIds);
    setShowDeleteConfirm(false);
  }

  function CustomToolbar() {
    const selectedDraftLessons = selectedLessons.filter(lesson => lesson.lessonState === LessonStateDto.Draft);
    const selectedPublishedLessons = selectedLessons.filter(lesson => lesson.lessonState === LessonStateDto.Published);
    const selectedEditableLessons = selectedPublishedLessons.filter(lesson => !isAttendanceSheetLocked(lesson.attendanceSheetState));
    const selectionCount = selectedDraftLessons.length + selectedPublishedLessons.length;
    return FasitDataGridToolbar({
      onAddButtonClick: !isCourseCompleted ? onAddLesson : undefined,
      onRefreshClick: onRefreshLessons,
      onFilterChange: handleFilterChange,
      title: open ? 'Aktuelle og planlagte lektioner' : 'Alle Lektioner',
      filterValues,
      activeFilter: filterValue,
      selectionCount,
      customSelectionInput: (
        <>
          {selectedDraftLessons.length > 0 && (
            <Tooltip title={`Publicér  ${selectedDraftLessons.length} kladder`}>
              <Button startIcon={<FontAwesomeIcon icon={faCheckCircle} />} onClick={() => setShowConfirm(true)} sx={{ py: '4px', px: '5px' }} />
            </Tooltip>
          )}
          {selectedEditableLessons.length > 0 && (
            <Tooltip title={`Aflys ${selectedEditableLessons.length} lektioner`}>
              <Button startIcon={<FontAwesomeIcon icon={faBan} />} onClick={() => setShowCancelConfirm(true)} sx={{ py: '4px', px: '5px' }} />
            </Tooltip>
          )}
          {selectedEditableLessons.length > 0 && (
            <Tooltip title={`Slet ${selectedEditableLessons.length} lektioner`}>
              <Button startIcon={<FontAwesomeIcon icon={faTrashCan} />} onClick={() => setShowDeleteConfirm(true)} sx={{ py: '4px', px: '5px' }} />
            </Tooltip>
          )}
        </>
      ),
    });
  }

  function getFilteredLessons() {
    const filtered = lessons?.filter(x => toSchultzDate(x.beginAt) > toStartOfDay(new Date()) && x.lessonState !== LessonStateDto.Cancelled);
    return (open ? filtered : lessons) || [];
  }

  function handleLessonSelectionChange(selectedIds: string[]) {
    const lessions = getFilteredLessons();
    const filteredLessons = lessions.filter(lesson => selectedIds.includes(lesson.lessonId));

    setSelectedLessons(filteredLessons);
  }

  function handleSetCandellationReason(event: React.ChangeEvent<HTMLInputElement>) {
    setCancellationReason(event.target.value);
    setCancellationReasonError(false);
  }

  const selectedDraftLessonsCount = selectedLessons.filter(x => x.lessonState === LessonStateDto.Draft).length;
  const selectedEditableLessonsCount = selectedLessons.filter(
    x => x.lessonState === LessonStateDto.Published && !isAttendanceSheetLocked(x.attendanceSheetState)
  ).length;
  return (
    <>
      <DataGridPro<LessonDto>
        columns={getColumns(classRooms, educators)}
        rows={getFilteredLessons()}
        autoHeight
        density="compact"
        onRowClick={({ row }) => handleEditLesson(row.lessonId)}
        getRowId={params => params.lessonId}
        pageSize={10}
        pagination
        checkboxSelection={!isCourseCompleted}
        disableSelectionOnClick
        components={{
          Toolbar: CustomToolbar,
        }}
        onSelectionModelChange={handleLessonSelectionChange}
        rowsPerPageOptions={[10]}
        sortModel={sortModel}
        onSortModelChange={(model: GridSortModel) => setSortModel(model)}
      />
      <ConfirmDialog
        title="Ønsker du at publicere lektionerne?"
        type="question"
        open={showConfirm || isSavingPublished}
        onConfirmClick={handleConfirmClick}
        onCancelClick={handleCancelClick}
        confirmButtonText="Ja, publicér lektioner"
        cancelButtonText="Afbryd"
        cancelIsPrimary={false}
        isLoading={isSavingPublished}
      >
        <p>Vil du publicere {selectedDraftLessonsCount > 1 ? `de ${selectedDraftLessonsCount} valgte lektioner` : 'den valgte lektion'}?</p>
      </ConfirmDialog>
      <ConfirmDialog
        title="Ønsker du at aflyse lektionerne?"
        type="warning"
        open={showCancelConfirm || isSavingCancelled}
        onConfirmClick={handleCancelLessonsConfirmClick}
        onCancelClick={handleCancelLessonsDismissClick}
        confirmButtonText="Ja, aflys lektioner"
        cancelButtonText="Afbryd"
        cancelIsPrimary={false}
        isLoading={isSavingCancelled}
        maxWidth="sm"
      >
        <p>Vil du aflyse {selectedEditableLessonsCount > 1 ? `de ${selectedEditableLessonsCount} valgte lektioner` : 'den valgte lektion'}?</p>
        <TextField
          label="Aflysningsårsag"
          value={cancellationReason}
          onChange={handleSetCandellationReason}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          sx={{ mt: 2 }}
          required
          error={cancellationReasonError}
          errorText="Aflysningsårsag skal udfyldes"
        />
      </ConfirmDialog>
      <DeleteLessonDialog
        isOpen={showDeleteConfirm}
        onConfirmClick={handleDeleteLessonsConfirmClick}
        onCancelClick={() => setShowDeleteConfirm(false)}
        lessonCount={selectedEditableLessonsCount}
        isLoading={isDeletingLessons}
      />
    </>
  );
}

function getColumns(classRooms: ClassroomDto[], educators: EducatorDto[]) {
  const columns: GridColDef[] = [
    {
      headerName: 'Dato',
      field: 'beginAt',
      width: 150,
      valueFormatter: params => formatDate(params.value),
    },
    {
      headerName: 'Uge',
      field: 'weekNumber',
      width: 100,
      valueGetter: ({ row }) => getISOWeek(new Date(row.beginAt)),
    },
    {
      headerName: 'Fra',
      field: 'startTime',
      width: 100,
      valueGetter: ({ row }) => formatDate(row.beginAt, 'HH:mm'),
    },
    {
      headerName: 'Til',
      field: 'endAt',
      width: 100,
      valueGetter: ({ row }) => formatDate(row.endAt, 'HH:mm'),
    },
    {
      headerName: 'Pladser',
      field: 'capacity',
      width: 100,
    },
    {
      headerName: 'Brugte pladser',
      field: 'occupiedSeats',
      width: 110,
    },
    {
      headerName: 'Undervisere',
      field: 'educatorIds',
      width: 200,
      valueFormatter: params =>
        educators
          .filter(x => params.value.includes(x.educatorId))
          .map(e => e.name)
          .join(', '),
    },
    {
      headerName: 'Lokale',
      field: 'classroomId',
      width: 200,
      valueFormatter: params => classRooms.find(x => x.classroomId === params.value)?.name,
    },
    {
      headerName: 'Status',
      field: 'lessonState',
      flex: 1,
      valueFormatter: params => getLessonStateDisplayValue(params.value),
    },
  ];
  return columns;
}
